import { emptySplitApi as baseApi } from "./baseApi";

const api = baseApi.enhanceEndpoints({
  addTagTypes: [
    "LabourBlock",
    "LabourWorker",
    "LabourAttendance",
    "LabourReport",
  ] as const,
});

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LabourBlock {
  id?: string;
  name: string;
  createdAt?: string;
}

export interface LabourWorker {
  id?: string;
  name: string;
  firstName?: string;
  surname?: string;
  nationalId?: string;
  email?: string;
  role: "Supervisor" | "Artisan" | "Assistant";
  jobTitle?: string;
  rate: number;
  active: boolean;
  defaultBlockId?: string;
  displayOrder?: number;
  createdAt?: string;
}

export interface LabourAttendance {
  id?: string;
  workerId: string;
  blockId: string;
  workDate: string;
  status: "present" | "absent";
}

export interface AttendanceBatchRow {
  workerId: string;
  blockId: string;
  workDate: string;
  status: "present" | "absent";
}

export interface LabourDashboard {
  workerCount: number;
  blockCount: number;
  todayPresent: number;
  cycleCost: number;
  cycleStart: string;
  cycleEnd: string;
  byBlock: { name: string; cost: number; manDays: number }[];
}

export interface LabourDailyReport {
  date: string;
  grandTotal: number;
  grandCount: number;
  byBlock: {
    blockId: string;
    blockName: string;
    rows: { name: string; role: string; wage: number; isCarpenter: boolean }[];
    total: number;
    artisans: number;
    assistants: number;
    supervisors: number;
    carpenters: number;
    carpenterCost: number;
  }[];
}

export interface LabourPayrollRow {
  workerId: string;
  name: string;
  role: string;
  rate: number;
  defaultBlock: string;
  daysPresent: number;
  pay: number;
}

export interface LabourPayrollReport {
  from: string;
  to: string;
  rows: LabourPayrollRow[];
  totalPay: number;
}

// ─── Endpoints ──────────────────────────────────────────────────────────────

const labourTrackerApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    // Blocks
    getLabourBlocks: build.query<LabourBlock[], void>({
      query: () => "/labour/blocks",
      providesTags: ["LabourBlock"],
    }),
    createLabourBlock: build.mutation<LabourBlock, { name: string }>({
      query: (body) => ({ url: "/labour/blocks", method: "POST", body }),
      invalidatesTags: ["LabourBlock"],
    }),
    updateLabourBlock: build.mutation<void, { id: string; name: string }>({
      query: ({ id, ...body }) => ({
        url: `/labour/blocks/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["LabourBlock"],
    }),
    deleteLabourBlock: build.mutation<void, string>({
      query: (id) => ({ url: `/labour/blocks/${id}`, method: "DELETE" }),
      invalidatesTags: ["LabourBlock"],
    }),

    // Workers
    getLabourWorkers: build.query<LabourWorker[], { active?: boolean } | void>({
      query: (params) => {
        const p = params as { active?: boolean } | undefined;
        if (p?.active !== undefined)
          return `/labour/workers?active=${p.active}`;
        return "/labour/workers";
      },
      providesTags: ["LabourWorker"],
    }),
    createLabourWorker: build.mutation<
      LabourWorker,
      Omit<LabourWorker, "id" | "createdAt" | "displayOrder">
    >({
      query: (body) => ({ url: "/labour/workers", method: "POST", body }),
      invalidatesTags: ["LabourWorker"],
    }),
    updateLabourWorker: build.mutation<
      void,
      { id: string; data: Partial<LabourWorker> }
    >({
      query: ({ id, data }) => ({
        url: `/labour/workers/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["LabourWorker"],
    }),
    deleteLabourWorker: build.mutation<void, string>({
      query: (id) => ({ url: `/labour/workers/${id}`, method: "DELETE" }),
      invalidatesTags: ["LabourWorker", "LabourAttendance"],
    }),

    // Attendance
    getLabourAttendance: build.query<
      LabourAttendance[],
      { from: string; to: string }
    >({
      query: ({ from, to }) => `/labour/attendance?from=${from}&to=${to}`,
      providesTags: ["LabourAttendance"],
    }),
    batchSaveAttendance: build.mutation<
      { saved: number },
      { rows: AttendanceBatchRow[] }
    >({
      query: (body) => ({
        url: "/labour/attendance/batch",
        method: "POST",
        body,
      }),
      invalidatesTags: ["LabourAttendance", "LabourReport"],
    }),

    // Reports
    getLabourDashboard: build.query<LabourDashboard, void>({
      query: () => "/labour/reports/dashboard",
      providesTags: ["LabourReport"],
    }),
    getLabourDailyReport: build.query<LabourDailyReport, string>({
      query: (date) => `/labour/reports/daily?date=${date}`,
      providesTags: ["LabourReport"],
    }),
    getLabourPayrollReport: build.query<
      LabourPayrollReport,
      { from?: string; to?: string } | void
    >({
      query: (params) => {
        const p = params as { from?: string; to?: string } | undefined;
        const qs = new URLSearchParams();
        if (p?.from) qs.set("from", p.from);
        if (p?.to) qs.set("to", p.to);
        const s = qs.toString();
        return s ? `/labour/reports/payroll?${s}` : "/labour/reports/payroll";
      },
      providesTags: ["LabourReport"],
    }),
    getLabourRangeReport: build.query<
      LabourPayrollReport,
      { from: string; to: string }
    >({
      query: ({ from, to }) => `/labour/reports/range?from=${from}&to=${to}`,
      providesTags: ["LabourReport"],
    }),
  }),
});

export const {
  useGetLabourBlocksQuery,
  useCreateLabourBlockMutation,
  useUpdateLabourBlockMutation,
  useDeleteLabourBlockMutation,
  useGetLabourWorkersQuery,
  useCreateLabourWorkerMutation,
  useUpdateLabourWorkerMutation,
  useDeleteLabourWorkerMutation,
  useGetLabourAttendanceQuery,
  useBatchSaveAttendanceMutation,
  useGetLabourDashboardQuery,
  useGetLabourDailyReportQuery,
  useGetLabourPayrollReportQuery,
  useGetLabourRangeReportQuery,
} = labourTrackerApi;
